import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Question, CheckCircle, ArrowDown, FilePdf, WarningCircle } from '@phosphor-icons/react';
import ProductCard from '../components/ProductCard';
import ExportCsvModal from '../components/ExportCsvModal';
import SavedBundlesList from '../components/SavedBundlesList';
import OrderTotalsPopover from '../components/OrderTotalsPopover';
import BundleSummaryCard from '../components/BundleSummaryCard';
import SelectionGuideModal from '../components/SelectionGuideModal';
import ImportOrderPdfModal from '../components/ImportOrderPdfModal';
import AlertDialog from '../components/AlertDialog';
import { rangeLabel, getFoundationBoxSize, groupSelections, computeBundleTotals, isSupportingPlusDisabled, isSupportingTotalCapReached, supportingCapFor, resolveProductCode, LARGE_SAVER_SIZES } from '../lib/bundleHelpers';
import { useAuth } from '../auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Distinct, subtle backgrounds for each range section. Keeps the dark theme cohesive
// while making the four ranges visually distinguishable.
const RANGE_SECTION_BG = {
  Core: 'border-gold/30 bg-gold/[0.04]',
  Performance: 'border-cyan-400/25 bg-cyan-500/[0.05]',
  Goal: 'border-orange-400/25 bg-orange-500/[0.05]',
  Essentials: 'border-emerald-400/25 bg-emerald-500/[0.05]',
};

// Test-id slug for each range — kebab/lowercase variant of the public range name
// (Core/Performance/Goal/Essentials) so QA can reason about the DOM consistently.
const RANGE_TESTID_SLUG = {
  Core: 'core',
  Performance: 'performance',
  Goal: 'goal',
  Essentials: 'essentials',
};

const buildAggregatedCsv = (savedBundles) => {
  const escape = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
  // Helper mirrors `lineUnitPrice` in bundleHelpers.js: prefer the snapshot, fall back to wholesale.
  const unitPriceOf = (g) => Math.round(((typeof g.unitPrice === 'number' && Number.isFinite(g.unitPrice))
    ? g.unitPrice : Number(g.wholesalePrice || 0)) * 100) / 100;
  const lines = ['Saver #,Saver Name,Product Code,Product Name,Range,Size,Flavour,Quantity,Unit Price,Line Total'];
  savedBundles.forEach((bundle) => {
    const num = `Saver ${String(bundle.number).padStart(2, '0')}`;
    groupSelections(bundle.products).forEach((g) => {
      const unit = unitPriceOf(g);
      lines.push([
        escape(num), escape(bundle.name), escape(resolveProductCode(g)), escape(g.name),
        escape(g.range || ''), escape(g.size || 'N/A'), escape(g.selectedFlavour || 'N/A'),
        g.qty, `$${unit.toFixed(2)}`, `$${(unit * g.qty).toFixed(2)}`,
      ].join(','));
    });
    const totals = computeBundleTotals(bundle);
    lines.push(`${escape(num + ' TOTALS')},,,,,,,,${escape('Subtotal $' + totals.subtotal.toFixed(2))},${escape('Saver Price $' + totals.bundlePrice.toFixed(2))}`);
    lines.push(`${escape(num + ' SAVING')},,,,,,,,,${escape('Saving $' + totals.customerSaving.toFixed(2) + ' (' + totals.savingPercent.toFixed(0) + '%)')}`);
    if (bundle.comments?.trim()) {
      lines.push(`${escape(num + ' COMMENTS')},${escape(bundle.comments)},,,,,,,,,`);
    }
    lines.push('');
  });
  return lines.join('\n');
};

export const BundleBuilder = () => {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);          // admin/SR: list of customers they can build for
  const [activeCustomerId, setActiveCustomerId] = useState(null); // whose context we're building in
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [proteinSize, setProteinSize] = useState('1kg');
  const [bundleName, setBundleName] = useState('Bundle 01');
  const [bundleComments, setBundleComments] = useState('');
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportModal, setExportModal] = useState(null);
  const [copyStatus, setCopyStatus] = useState('idle');
  const [discounts, setDiscounts] = useState({
    small_single_pct: 10, small_box_pct: 10, large_single_pct: 10, large_box_pct: 10,
    small_pallet_pct: 10, large_pallet_pct: 10,
  });
  const [hiddenProductIds, setHiddenProductIds] = useState([]);
  const [productDiscounts, setProductDiscounts] = useState({}); // productId -> pct off RRP for the active customer
  const [customerSmartSaverEnabled, setCustomerSmartSaverEnabled] = useState(true);
  const [customerSingleTubEnabled, setCustomerSingleTubEnabled] = useState(false);
  // Admin/SR with NO customer selected: Single Tub hidden by default but toggleable.
  const [adminSingleTubPreview, setAdminSingleTubPreview] = useState(false);
  const [savedBundles, setSavedBundles] = useState([]);
  const [expandedBundleIds, setExpandedBundleIds] = useState(new Set());
  const [boxModeProductId, setBoxModeProductId] = useState(null);
  const [singleModeProductId, setSingleModeProductId] = useState(null);
  const [palletModeProductId, setPalletModeProductId] = useState(null);
  const [enabledSupportingIds, setEnabledSupportingIds] = useState(new Set());
  const [guideOpen, setGuideOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [notice, setNotice] = useState(null); // { title, message, variant }

  // ---- Data fetching ----
  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products?active_only=true`);
      setProducts(res.data);
    } catch (e) {
      console.error('Error fetching products:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // For admin/SR: load the customer list so they can pick who they're building for.
  const fetchCustomers = useCallback(async () => {
    if (!me || me.role === 'customer') return;
    try {
      const res = await axios.get(`${API}/users`);
      setAllCustomers(res.data.filter((u) => u.role === 'customer' && u.active));
    } catch (e) {
      console.error('Error fetching customers:', e);
    }
  }, [me]);

  // Load effective config (discounts + hidden products) for the active context.
  // For a customer: their own. For admin/SR: the selected customer's, or the global discounts when none picked.
  const fetchEffectiveConfig = useCallback(async () => {
    if (!me) return;
    const targetId = me.role === 'customer' ? me.id : activeCustomerId;
    if (!targetId) {
      // Admin/SR with no customer chosen: use global discounts and show every product.
      try {
        const res = await axios.get(`${API}/settings`);
        setDiscounts(res.data);
      } catch (e) {
        console.error('Error fetching discounts:', e);
      }
      setHiddenProductIds([]);
      setProductDiscounts({});
      setCustomerSmartSaverEnabled(true);
      setCustomerSingleTubEnabled(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/users/${targetId}/effective-config`);
      setDiscounts(res.data.discounts);
      setHiddenProductIds(res.data.hiddenProductIds || []);
      setProductDiscounts(res.data.productDiscounts || {});
      setCustomerSmartSaverEnabled(res.data.smartSaverEnabled !== false);
      setCustomerSingleTubEnabled(res.data.singleTubEnabled === true);
    } catch (e) {
      console.error('Error fetching effective config:', e);
    }
  }, [me, activeCustomerId]);

  // Customers whose Smart Saver is disabled get bounced to Browse & Order so they
  // don't see a useless page. Admin/Sales-Rep stay so they can see the warning and act on it.
  useEffect(() => {
    if (me?.role === 'customer' && me?.smartSaverEnabled === false) {
      navigate('/browse', { replace: true });
    }
  }, [me, navigate]);

  // Reload saved bundles whenever the active context changes.
  const fetchSavedBundles = useCallback(async () => {
    if (!me) return;
    const targetId = me.role === 'customer' ? me.id : activeCustomerId;
    if (!targetId) { setSavedBundles([]); return; }
    try {
      const res = await axios.get(`${API}/saved-bundles?customerId=${targetId}`);
      setSavedBundles(res.data);
      // Set the next bundle name based on current count.
      setBundleName(`Saver ${String((res.data?.length || 0) + 1).padStart(2, '0')}`);
    } catch (e) {
      console.error('Error fetching saved bundles:', e);
    }
  }, [me, activeCustomerId]);

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
  }, [fetchProducts, fetchCustomers]);

  useEffect(() => {
    fetchEffectiveConfig();
    fetchSavedBundles();
    // Reset the in-progress builder when active customer changes
    setSelectedProducts([]);
    setBundleComments('');
    setValidation(null);
    setBoxModeProductId(null);
    setSingleModeProductId(null);
    setPalletModeProductId(null);
    setEnabledSupportingIds(new Set());
  }, [fetchEffectiveConfig, fetchSavedBundles]);

  // ---- Validation ----
  const validateBundle = useCallback(async (productsToValidate, size, tier) => {
    try {
      const res = await axios.post(`${API}/bundles/validate`, {
        proteinSize: size,
        selectedProducts: productsToValidate.map((p) => p.id),
        selectedFlavours: productsToValidate.map((p) => p.selectedFlavour || null),
        tier,
      });
      setValidation(res.data);
    } catch (e) {
      console.error('Error validating bundle:', e);
    }
  }, []);

  useEffect(() => {
    const coreProduct = selectedProducts.find((p) => p.range === 'Core' && p.size);
    if (coreProduct && coreProduct.size !== proteinSize) {
      setProteinSize(coreProduct.size);
      return; // proteinSize will change → effect re-runs.
    }
    // Determine tier inline (currentTier helper isn't memoised so we re-derive here).
    const tier = palletModeProductId ? 'pallet' : (boxModeProductId ? 'box' : 'single');
    if (selectedProducts.length > 0) {
      validateBundle(selectedProducts, proteinSize, tier);
    } else {
      setValidation(null);
    }
  }, [selectedProducts, proteinSize, validateBundle, palletModeProductId, boxModeProductId]);

  // Effective validation: backend rules layer, plus the Box-tier count window.
  // Box tier (Jun 2026): at least ONE full box, partials on top — any Core count
  // from boxSize up to just under a pallet (5 boxes). 1kg: 8–39, 2kg: 6–29.
  //
  // Pallet mode keeps the backend ruling as-is (already enforces 5-box minimum +
  // supporting ratio), so no extra layering needed.
  const effectiveValidation = useMemo(() => {
    if (palletModeProductId) return validation;
    if (!boxModeProductId) return validation;
    const boxProduct = products.find((p) => p.id === boxModeProductId);
    if (!boxProduct) return validation;
    const boxSize = getFoundationBoxSize(boxProduct);
    if (!boxSize) return validation;
    const foundationCount = selectedProducts.filter((p) => p.range === 'Core').length;
    const palletMin = 5 * boxSize;
    if (foundationCount >= boxSize && foundationCount < palletMin) {
      return validation;
    }
    if (foundationCount >= palletMin) {
      return {
        valid: false,
        status: 'incomplete',
        message: `Box tier caps at ${palletMin - 1} tubs — for ${palletMin}+ (5 boxes) switch to the Pallet Layer tier.`,
        missing: ['Core box'],
      };
    }
    return {
      valid: false,
      status: 'incomplete',
      message: `Box tier needs at least one full box (${boxSize} Core tubs — currently ${foundationCount}). Partial extras on top are welcome, up to ${palletMin - 1} tubs.`,
      missing: ['Core box'],
    };
  }, [validation, boxModeProductId, palletModeProductId, products, selectedProducts]);

  // ---- Selection handlers ----
  const handleSelectProduct = (product) => {
    const exists = selectedProducts.find((p) => p.id === product.id);
    if (exists) {
      setSelectedProducts(selectedProducts.filter((p) => p.id !== product.id));
      return;
    }
    // Cap gates (mirrors the +/- stepper gating) — silently block when the TOTAL
    // supporting cap (1/tub 1kg, 2/tub 2kg) or the per-range cap would be exceeded.
    if (product.range !== 'Core'
        && (isSupportingTotalCapReached(selectedProducts, proteinSize)
            || isSupportingPlusDisabled(product, selectedProducts))) {
      return;
    }
    const withFlavour = { ...product, selectedFlavour: product.flavours?.[0] || null };
    if (product.range === 'Core') {
      if (product.size) setProteinSize(product.size);
      setSelectedProducts([...selectedProducts.filter((p) => p.range !== 'Core'), withFlavour]);
    } else {
      setSelectedProducts([...selectedProducts, withFlavour]);
    }
  };

  const handleToggleFlavour = (product, flavour) => {
    const existing = selectedProducts.find((p) => p.id === product.id && p.selectedFlavour === flavour);
    if (existing) {
      setSelectedProducts(selectedProducts.filter((p) => !(p.id === product.id && p.selectedFlavour === flavour)));
      return;
    }
    // Cap gates (mirrors the +/- stepper gating) — total supporting cap + per-range cap.
    if (product.range !== 'Core'
        && (isSupportingTotalCapReached(selectedProducts, proteinSize)
            || isSupportingPlusDisabled(product, selectedProducts))) {
      return;
    }
    let base = selectedProducts;
    if (product.range === 'Core') {
      // Foundation Single mode: only ONE tub allowed total — drop any other Core selections
      // INCLUDING other flavours of the same product. (Box mode is handled by handleAdjustBoxQty.)
      const inSingleMode = singleModeProductId === product.id;
      base = inSingleMode
        ? base.filter((p) => p.range !== 'Core')
        : base.filter((p) => p.range !== 'Core' || p.id === product.id);
      if (product.size) setProteinSize(product.size);
      if (boxModeProductId && boxModeProductId !== product.id) setBoxModeProductId(null);
    }
    setSelectedProducts([...base, { ...product, selectedFlavour: flavour }]);
  };

  const getFlavourQty = (productId, flavour) =>
    selectedProducts.filter((p) => p.id === productId && p.selectedFlavour === flavour).length;
  const getProductQty = (productId) =>
    selectedProducts.filter((p) => p.id === productId).length;

  const handleAdjustBoxQty = (product, flavour, delta) => {
    if (delta > 0) {
      // Supporting adds are blocked once the TOTAL supporting cap is reached.
      if (product.range !== 'Core' && isSupportingTotalCapReached(selectedProducts, proteinSize)) return;
      let base = selectedProducts;
      if (product.range === 'Core') {
        base = base.filter((p) => p.range !== 'Core' || p.id === product.id);
        if (product.size) setProteinSize(product.size);
      }
      setSelectedProducts([...base, { ...product, selectedFlavour: flavour || null }]);
      return;
    }
    let removed = false;
    setSelectedProducts(selectedProducts.filter((p) => {
      if (!removed && p.id === product.id && (p.selectedFlavour || null) === (flavour || null)) {
        removed = true;
        return false;
      }
      return true;
    }));
  };

  // Manual quantity entry (typed number next to +/−) — works for supporting
  // products AND Core flavour rows.
  const handleSetSupportingQty = (product, flavour, value) => {
    let target = Math.max(0, Math.floor(Number(value) || 0));
    if (product.range !== 'Core' && LARGE_SAVER_SIZES.has(proteinSize) && !palletModeProductId) {
      target = Math.min(target, Math.max(1, foundationCount)); // SKU cap: 1 per 2 kg tub
    }
    if (product.range !== 'Core') {
      // TOTAL supporting cap: typed values can never push the order-wide supporting
      // count past 1/tub (1kg) or 2/tub (2kg).
      const cap = supportingCapFor(selectedProducts, proteinSize);
      if (cap !== null) {
        const currentRow = selectedProducts.filter(
          (p) => p.id === product.id && (p.selectedFlavour || null) === (flavour || null)
        ).length;
        const otherSupporting = selectedProducts.filter((p) => p.range !== 'Core').length - currentRow;
        target = Math.min(target, Math.max(0, cap - otherSupporting));
      }
    }
    target = Math.min(target, 999);
    let base = selectedProducts;
    if (product.range === 'Core') {
      base = base.filter((p) => p.range !== 'Core' || p.id === product.id);
      if (product.size) setProteinSize(product.size);
    }
    const others = base.filter(
      (p) => !(p.id === product.id && (p.selectedFlavour || null) === (flavour || null))
    );
    const additions = Array.from({ length: target }, () => ({ ...product, selectedFlavour: flavour || null }));
    setSelectedProducts([...others, ...additions]);
  };

  const handleToggleBoxMode = (product) => {
    const turningOn = boxModeProductId !== product.id;
    // Seamless pallet → box downgrade: keep this product's Core tubs + all supporting.
    if (turningOn && palletModeProductId === product.id) {
      setSelectedProducts(selectedProducts.filter((p) => p.range !== 'Core' || p.id === product.id));
    } else {
      setSelectedProducts(selectedProducts.filter((p) => p.range !== 'Core'));
    }
    setBoxModeProductId(turningOn ? product.id : null);
    if (turningOn) { setSingleModeProductId(null); setPalletModeProductId(null); }
    if (turningOn && product.size) setProteinSize(product.size);
  };

  const handleToggleSingleMode = (product) => {
    const turningOn = singleModeProductId !== product.id;
    setSelectedProducts(selectedProducts.filter((p) => p.range !== 'Core'));
    setSingleModeProductId(turningOn ? product.id : null);
    if (turningOn) { setBoxModeProductId(null); setPalletModeProductId(null); }
    if (turningOn && product.size) setProteinSize(product.size);
  };

  // Pallet Layer mode (5+ boxes) — same Core stepper UI as Box, but the
  // supporting cap is relaxed (free choice across ranges) and pricing uses
  // the *_pallet_pct tier. Added Feb 25 2026.
  const handleTogglePalletMode = (product) => {
    const turningOn = palletModeProductId !== product.id;
    // Seamless box → pallet upgrade: keep this product's Core tubs + all supporting.
    if (turningOn && boxModeProductId === product.id) {
      setSelectedProducts(selectedProducts.filter((p) => p.range !== 'Core' || p.id === product.id));
    } else {
      setSelectedProducts(selectedProducts.filter((p) => p.range !== 'Core'));
    }
    setPalletModeProductId(turningOn ? product.id : null);
    if (turningOn) { setBoxModeProductId(null); setSingleModeProductId(null); }
    if (turningOn && product.size) setProteinSize(product.size);
  };

  // One-click tier switch from the Saver Summary alerts — keeps the whole selection.
  const switchTierKeepingSelection = (toTier) => {
    const coreProductId = boxModeProductId || palletModeProductId;
    if (!coreProductId) return;
    if (toTier === 'pallet') {
      setPalletModeProductId(coreProductId);
      setBoxModeProductId(null);
    } else {
      setBoxModeProductId(coreProductId);
      setPalletModeProductId(null);
    }
    setSingleModeProductId(null);
  };

  const handleToggleEnabled = (product) => {
    const isEnabled = enabledSupportingIds.has(product.id);
    const hasFlavours = (product.flavours?.length || 0) > 0;
    const inBoxMode = !!boxModeProductId;

    if (isEnabled) {
      // Disabling: clear any selections of this product
      setEnabledSupportingIds((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
      setSelectedProducts(selectedProducts.filter((p) => p.id !== product.id));
      return;
    }

    setEnabledSupportingIds((prev) => {
      const next = new Set(prev);
      next.add(product.id);
      return next;
    });
    // For no-flavour products in single mode, the checkbox IS the selection — add 1 instance.
    // (In Box mode the user uses the +/- counter to set quantity; in single mode with flavours they
    // tick a flavour checkbox.) Respect the per-range cap.
    if (!hasFlavours && !inBoxMode) {
      if (isSupportingTotalCapReached(selectedProducts, proteinSize)
          || isSupportingPlusDisabled(product, selectedProducts)) return;
      setSelectedProducts([...selectedProducts, { ...product, selectedFlavour: null }]);
    }
  };

  // Pick the discount % based on size + tier. tier is 'single' | 'box' | 'pallet'.
  // Discount percentages are capped at 1 decimal place so manual customer math reconciles.
  const pickDiscountPct = (size, tier) => {
    const isLarge = LARGE_SAVER_SIZES.has(size);
    let pct;
    if (tier === 'pallet') pct = isLarge ? discounts.large_pallet_pct : discounts.small_pallet_pct;
    else if (tier === 'box') pct = isLarge ? discounts.large_box_pct : discounts.small_box_pct;
    else pct = isLarge ? discounts.large_single_pct : discounts.small_single_pct;
    return Math.round((Number(pct) || 0) * 10) / 10;
  };

  // Helper — what tier is the saver currently being built in?
  const currentTier = () => {
    if (palletModeProductId) return 'pallet';
    if (boxModeProductId) return 'box';
    return 'single';
  };

  // ---- Saved bundles ----
  // Resolve who the bundle belongs to. Admin/SR must pick a customer first.
  const activeContextCustomerId = me?.role === 'customer' ? me.id : activeCustomerId;

  const handleSaveBundle = async () => {
    if (!effectiveValidation?.valid) return;
    if (!activeContextCustomerId) {
      setNotice({ title: 'Pick a customer', message: 'Pick a customer to build for before saving.', variant: 'warning' });
      return;
    }
    const tier = currentTier();
    const discountPct = pickDiscountPct(proteinSize, tier);
    const payload = {
      customerId: activeContextCustomerId,
      name: bundleName,
      comments: bundleComments,
      proteinSize,
      discountPct,
      tier,
      products: selectedProducts.map((p) => {
        // Snapshot the customer unit price (ex-GST) at save time. If a per-product
        // override exists for this customer, unitPrice = (RRP / 1.15) × (1 − pct/100);
        // else fall back to the product's stored wholesalePrice (already ex-GST).
        const pct = productDiscounts[p.id];
        const rawUnit = (typeof pct === 'number' && Number.isFinite(pct))
          ? (Number(p.rrp) / 1.15) * (1 - pct / 100)
          : Number(p.wholesalePrice);
        const unitPrice = Math.round(rawUnit * 100) / 100;
        return {
          id: p.id, name: p.name, flavourCodes: p.flavourCodes || {},
          range: p.range, size: p.size || null,
          wholesalePrice: p.wholesalePrice, rrp: p.rrp, selectedFlavour: p.selectedFlavour || null,
          unitPrice,
        };
      }),
    };
    try {
      await axios.post(`${API}/saved-bundles`, payload);
      await fetchSavedBundles();
    } catch (e) {
      console.error('Save bundle failed:', e);
      setNotice({ title: 'Save failed', message: e.response?.data?.detail || 'Save failed', variant: 'warning' });
      return;
    }
    setSelectedProducts([]);
    setBundleComments('');
    setValidation(null);
    setBoxModeProductId(null);
    setSingleModeProductId(null);
    setPalletModeProductId(null);
    setEnabledSupportingIds(new Set());
  };

  const handleRemoveBundle = async (id) => {
    try {
      await axios.delete(`${API}/saved-bundles/${id}`);
      await fetchSavedBundles();
    } catch (e) {
      console.error('Delete bundle failed:', e);
    }
    setExpandedBundleIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleDuplicateBundle = async (id) => {
    const original = savedBundles.find((b) => b.id === id);
    if (!original || !activeContextCustomerId) return;
    try {
      await axios.post(`${API}/saved-bundles`, {
        customerId: activeContextCustomerId,
        name: `${original.name} (copy)`,
        comments: original.comments || '',
        proteinSize: original.proteinSize,
        discountPct: original.discountPct,
        products: original.products.map((p) => ({ ...p })),
      });
      await fetchSavedBundles();
    } catch (e) {
      console.error('Duplicate bundle failed:', e);
    }
  };

  const handleClearAll = async () => {
    if (!activeContextCustomerId) {
      throw new Error('Pick a customer first.');
    }
    await axios.delete(`${API}/saved-bundles?customerId=${activeContextCustomerId}`);
    await fetchSavedBundles();
    setExpandedBundleIds(new Set());
  };

  const handleSubmitOrder = async ({ prepayRequested = false, customerReference = '' } = {}) => {
    if (!activeContextCustomerId) {
      const err = new Error('Pick a customer first.');
      throw err;
    }
    const flag = prepayRequested ? '&prepayRequested=true' : '';
    const ref = (customerReference || '').trim();
    const refParam = ref ? `&customerReference=${encodeURIComponent(ref)}` : '';
    const res = await axios.post(`${API}/orders/submit?customerId=${activeContextCustomerId}&acceptedTerms=true${flag}${refParam}`);
    await fetchSavedBundles();
    setExpandedBundleIds(new Set());
    return res.data;
  };

  // Snapshot current saved bundles into a named saved_order. Active bundles are kept.
  const handleSaveOrder = async (name) => {
    if (!activeContextCustomerId) {
      throw new Error('Pick a customer first.');
    }
    const res = await axios.post(`${API}/saved-orders`, {
      customerId: activeContextCustomerId,
      name,
    });
    return res.data;
  };

  // Load a saved bundle back into the builder for editing.
  // Strategy: delete the server-side bundle, then re-hydrate the builder state
  // from its products. The user can then tweak and click Save again.
  const handleEditBundle = async (id) => {
    const bundle = savedBundles.find((b) => b.id === id);
    if (!bundle) return;
    try {
      await axios.delete(`${API}/saved-bundles/${id}`);
    } catch (e) {
      console.error('Edit/delete failed:', e);
      return;
    }
    // Hydrate builder state from the bundle's snapshot
    const allProducts = products;
    const hydrated = bundle.products.map((p) => {
      const live = allProducts.find((lp) => lp.id === p.id) || {};
      // Merge live product fields (so flavours list is current) with the saved per-line state
      return { ...live, ...p };
    });
    setSelectedProducts(hydrated);
    setProteinSize(bundle.proteinSize);
    setBundleName(bundle.name);
    setBundleComments(bundle.comments || '');
    // Detect mode based on the saved bundle's `tier`. For legacy saves with no
    // `tier`, fall back to the count heuristic (≥2 cores = Box, 1 core = Single).
    const coreItems = hydrated.filter((p) => p.range === 'Core');
    const coreCounts = coreItems.reduce((acc, p) => { acc[p.id] = (acc[p.id] || 0) + 1; return acc; }, {});
    const coreIds = Object.keys(coreCounts);
    const saved_tier = bundle.tier;
    if (saved_tier === 'pallet' && coreIds.length === 1) {
      setPalletModeProductId(coreIds[0]);
      setBoxModeProductId(null);
      setSingleModeProductId(null);
    } else if (saved_tier === 'box' && coreIds.length === 1) {
      setBoxModeProductId(coreIds[0]);
      setSingleModeProductId(null);
      setPalletModeProductId(null);
    } else if (saved_tier === 'single' && coreIds.length === 1) {
      setSingleModeProductId(coreIds[0]);
      setBoxModeProductId(null);
      setPalletModeProductId(null);
    } else if (coreIds.length === 1 && coreCounts[coreIds[0]] > 1) {
      setBoxModeProductId(coreIds[0]);
      setSingleModeProductId(null);
      setPalletModeProductId(null);
    } else if (coreIds.length === 1) {
      setBoxModeProductId(null);
      setSingleModeProductId(coreIds[0]);
      setPalletModeProductId(null);
    } else {
      setBoxModeProductId(null);
      setSingleModeProductId(null);
      setPalletModeProductId(null);
    }
    // Auto-enable any supporting products that appear in the bundle so their UI shows immediately
    setEnabledSupportingIds(new Set(hydrated.filter((p) => p.range !== 'Core').map((p) => p.id)));
    await fetchSavedBundles();
    setExpandedBundleIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    // Scroll to the top of the page for clarity
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleBundleExpansion = (id) => {
    setExpandedBundleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ---- CSV export ----
  const handleExportAll = () => {
    if (savedBundles.length === 0) return;
    setExportModal({
      csv: buildAggregatedCsv(savedBundles),
      filename: `bundles_export_${new Date().toISOString().slice(0, 10)}.csv`,
    });
  };

  const downloadFromModal = () => {
    if (!exportModal) return;
    const blob = new Blob([exportModal.csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportModal.filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.open(url, '_blank', 'noopener,noreferrer'), 100);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const copyCsvToClipboard = async () => {
    if (!exportModal) return;
    try {
      await navigator.clipboard.writeText(exportModal.csv);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch {
      const ta = document.getElementById('export-csv-textarea');
      if (ta) ta.select();
      setCopyStatus('select');
      setTimeout(() => setCopyStatus('idle'), 3000);
    }
  };

  const productsByRange = useMemo(() => {
    const hiddenSet = new Set(hiddenProductIds);
    // Smart Saver only renders products explicitly flagged as bundleable. Anything
    // marked `isBundleable=false` (catalogue-only items like Burn, Critical Mass) is
    // hidden from the builder entirely, regardless of range.
    const visible = products.filter((p) => !hiddenSet.has(p.id) && p.isBundleable !== false);
    // Order is meaningful for the UI: Core → Performance → Goal → Essentials.
    return {
      Core: visible.filter((p) => p.range === 'Core'),
      Performance: visible.filter((p) => p.range === 'Performance'),
      Goal: visible.filter((p) => p.range === 'Goal'),
      Essentials: visible.filter((p) => p.range === 'Essentials'),
    };
  }, [products, hiddenProductIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-text-secondary">Loading products...</p>
      </div>
    );
  }

  const discountPct = pickDiscountPct(proteinSize, currentTier());

  // Single Tub tier availability: per-customer flag when a context is active;
  // admin/SR with no customer selected use a local preview toggle (default hidden).
  const singleTubAllowed = activeContextCustomerId ? customerSingleTubEnabled : adminSingleTubPreview;

  // When the bundle is fully valid, every un-selected entry point across all cards
  // is locked. The user must deselect something before adding more.
  const bundleSatisfied = effectiveValidation?.valid === true;

  // Per-range "cap reached" state for visual dimming.
  // Once a range's selection limit is fulfilled within the current Saver, any
  // un-engaged product in that range is greyed out until the user deselects an item.
  const foundationCount = selectedProducts.filter((p) => p.range === 'Core').length;
  // TOTAL supporting cap across ALL categories — once hit, every supporting add
  // option is unavailable regardless of category (1/tub 1kg, 2/tub 2kg).
  const supportingTotalCount = selectedProducts.filter((p) => p.range !== 'Core').length;
  const supportingCapTotal = supportingCapFor(selectedProducts, proteinSize);
  const supportingCapReached = supportingCapTotal !== null && supportingTotalCount >= supportingCapTotal;
  const coreEngagedProductId = boxModeProductId
    || singleModeProductId
    || palletModeProductId
    || (selectedProducts.find((p) => p.range === 'Core')?.id || null);
  const coreRangeEngaged = !!coreEngagedProductId;
  const supportingRangeFull = (rangeName) => {
    if (foundationCount === 0) return false;
    // Pallet mode: no per-range cap, so a range is never "full".
    if (palletModeProductId) return false;
    const items = selectedProducts.filter((p) => p.range === rangeName);
    const aminos = items.filter((p) => p.name === 'Pure Series Aminos').length;
    const effective = items.length - Math.max(0, aminos - 1);
    return effective >= foundationCount;
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-2" data-testid="page-title">Raisey&apos;s Smart Saver</h1>
            <p className="text-text-secondary">Build Smart, Save Smart</p>
          </div>
          <div className="flex items-start gap-3 shrink-0">
            {me && me.role !== 'customer' && (
              <div className="flex flex-col" data-testid="customer-selector-wrap">
                <label className="text-xs text-text-secondary mb-1">Building for</label>
                <select
                  value={activeCustomerId || ''}
                  onChange={(e) => setActiveCustomerId(e.target.value || null)}
                  data-testid="customer-selector"
                  className="px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none min-w-[220px]"
                >
                  <option value="">— Select a customer —</option>
                  {allCustomers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                  ))}
                </select>
                {!activeCustomerId && (
                  <label className="mt-1.5 flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={adminSingleTubPreview}
                      onChange={(e) => setAdminSingleTubPreview(e.target.checked)}
                      data-testid="admin-single-tub-preview-toggle"
                      className="w-3.5 h-3.5 accent-yellow-500"
                    />
                    Show Single Tub tier
                  </label>
                )}
              </div>
            )}
            {me && me.role !== 'customer' && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                data-testid="open-import-pdf-button"
                aria-label="Import order PDF"
                className="mt-5 flex items-center gap-2 px-3 py-2 border border-border-color text-text-primary text-sm rounded-lg hover:bg-surface-hover transition-colors"
                title="Upload an order PDF and auto-pack into savers"
              >
                <FilePdf size={18} weight="duotone" className="text-gold" />
                <span className="hidden sm:inline">Import Order PDF</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              data-testid="open-guide-button"
              aria-label="Open Smart Saver help"
              className="mt-5 flex items-center gap-2 px-3 py-2 border border-gold/60 text-gold text-sm rounded-lg hover:bg-gold hover:text-black transition-colors"
            >
              <Question size={18} weight="bold" />
              <span className="hidden sm:inline">Help</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {!customerSmartSaverEnabled && (
              <div
                className="rounded-2xl border border-warning/40 bg-warning/[0.08] p-5 flex items-start gap-3"
                data-testid="smart-saver-disabled-notice"
              >
                <WarningCircle size={22} weight="duotone" className="text-warning shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-warning mb-1">Smart Saver disabled for this customer</p>
                  <p className="text-xs text-text-secondary leading-snug">
                    {me?.role === 'customer'
                      ? "Smart Saver isn't enabled on your account. Head to Browse & Order to place your order, or contact us to enable Smart Saver."
                      : "This customer has Smart Saver disabled in their account overrides. Saves will be rejected by the server. Use Browse & Order for them, or re-enable Smart Saver from User Management → Overrides."}
                  </p>
                </div>
              </div>
            )}
            {customerSmartSaverEnabled && bundleSatisfied && (
              <div
                className="rounded-2xl border border-success/40 bg-success/[0.08] p-4 flex items-center justify-between gap-4"
                data-testid="bundle-complete-banner"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <CheckCircle size={24} weight="duotone" className="text-success shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-success">Saver complete</p>
                    <p className="text-xs text-text-secondary leading-snug">
                      All selection rules satisfied. New picks are locked until you deselect — or
                      open the summary to save.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.querySelector('[data-testid="bundle-summary"]');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  data-testid="tweak-selections-button"
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-success hover:text-success/80 underline underline-offset-4 decoration-success/40 hover:decoration-success"
                >
                  Tweak selections
                  <ArrowDown size={14} weight="bold" />
                </button>
              </div>
            )}
            {Object.entries(productsByRange).map(([range, rangeProducts]) => {
              if (!customerSmartSaverEnabled) return null;
              const slug = RANGE_TESTID_SLUG[range] || range.toLowerCase();
              // Core products share a sized "family" (e.g. PRO75 = 1kg + 2kg; ISO90 = 900g + 1.8kg).
              // Group them so all variants of the same brand stack together under one heading.
              // Other ranges keep the flat grid (no sized variants).
              const isCoreRange = range === 'Core';
              const sizeToGrams = (sz) => {
                if (!sz) return 0;
                const m = String(sz).match(/^([\d.]+)\s*(kg|g)$/i);
                if (!m) return 0;
                return parseFloat(m[1]) * (m[2].toLowerCase() === 'kg' ? 1000 : 1);
              };
              const familyOf = (p) => (p.size ? p.name.replace(new RegExp(`\\s+${p.size.replace('.', '\\.')}$`), '').trim() : p.name);
              // Display order for Core families: PRO75 first, ISO90 second, then
              // anything else alphabetically. (Per Raisey's brand-hierarchy choice
              // Feb 26 2026.)
              const FAMILY_ORDER = ['PRO75', 'ISO90'];
              const familyRank = (fam) => {
                const i = FAMILY_ORDER.indexOf(fam);
                return i === -1 ? FAMILY_ORDER.length + 1 : i;
              };
              const coreFamilies = [];
              if (isCoreRange) {
                const grouped = new Map();
                for (const p of rangeProducts) {
                  const fam = familyOf(p);
                  if (!grouped.has(fam)) grouped.set(fam, []);
                  grouped.get(fam).push(p);
                }
                for (const [fam, items] of grouped) {
                  items.sort((a, b) => sizeToGrams(a.size) - sizeToGrams(b.size));
                  coreFamilies.push({ family: fam, items });
                }
                coreFamilies.sort((a, b) => {
                  const ra = familyRank(a.family);
                  const rb = familyRank(b.family);
                  if (ra !== rb) return ra - rb;
                  return a.family.localeCompare(b.family);
                });
              }
              return (
              <section
                key={range}
                className={`rounded-2xl border p-5 ${RANGE_SECTION_BG[range] || 'border-border-color bg-surface/40'}`}
                data-testid={`range-section-${slug}`}
              >
                <h2
                  className="text-xl font-bold font-heading text-gold mb-4"
                  data-testid={`range-${slug}`}
                >
                  {rangeLabel(range)}
                </h2>
                <div className={isCoreRange && coreFamilies.length === 1 ? '' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                  {isCoreRange
                    ? coreFamilies.map(({ family, items }) => {
                        // Family branding: PRO75 stays brand-gold, ISO90 uses
                        // emerald. Each family also gets a subtle tinted card
                        // wrapper with a coloured left border so the two
                        // brands read as distinct groups even at a glance.
                        const FAMILY_STYLE = {
                          PRO75: {
                            heading: 'text-gold',
                            wrapper: 'border-l-4 border-gold/70 bg-gold/[0.04]',
                          },
                          ISO90: {
                            heading: 'text-emerald-400',
                            wrapper: 'border-l-4 border-emerald-500/70 bg-emerald-500/[0.05]',
                          },
                        };
                        const style = FAMILY_STYLE[family] || {
                          heading: 'text-text-primary',
                          wrapper: 'border-l-4 border-border-color bg-surface/30',
                        };
                        // With a single Core family, its size variants sit side-by-side
                        // across the full row width instead of stacking in a half-width column.
                        const singleFamily = coreFamilies.length === 1;
                        return (
                        <div
                          key={family}
                          className={`flex flex-col gap-3 rounded-lg p-3 pl-4 ${style.wrapper}`}
                          data-testid={`core-family-${family.toLowerCase()}`}
                        >
                          <h3
                            className={`text-2xl font-heading font-bold tracking-tight ${style.heading}`}
                            data-testid={`core-family-heading-${family.toLowerCase()}`}
                          >
                            {family}
                          </h3>
                          {family === 'PRO75' && (
                            <p className="text-xs text-text-secondary -mt-1.5" data-testid="core-family-tagline-pro75">
                              NZ-made whey protein concentrate — the foundation of every saver.
                            </p>
                          )}
                          <div className={singleFamily ? 'grid grid-cols-1 md:grid-cols-2 gap-4 items-start' : 'flex flex-col gap-3'}>
                          {items.map((product) => {
                            const isMultiFlavour = product.flavours?.length > 0;
                            const instances = selectedProducts.filter((p) => p.id === product.id);
                            const selected = instances[0];
                            const selectedFlavours = instances.map((p) => p.selectedFlavour).filter(Boolean);
                            const boxSize = getFoundationBoxSize(product);
                            const boxModeActive = boxModeProductId === product.id;
                            const singleModeActive = singleModeProductId === product.id;
                            const palletModeActive = palletModeProductId === product.id;
                            const lockNewSelections = bundleSatisfied && instances.length === 0;
                            // Core range fits ONE Foundation product per Saver. Dim every other
                            // Core card once one is engaged (selected, in box mode, or single mode).
                            const dimmed = coreRangeEngaged && coreEngagedProductId !== product.id;
                            return (
                              <ProductCard
                                key={product.id}
                                product={product}
                                isSelected={instances.length > 0}
                                selectedFlavour={selected?.selectedFlavour}
                                selectedFlavours={selectedFlavours}
                                onSelect={handleSelectProduct}
                                onToggleFlavour={handleToggleFlavour}
                                multiFlavourMode={isMultiFlavour}
                                boxSize={boxSize}
                                boxMode={boxModeActive}
                                singleMode={singleModeActive}
                                palletMode={palletModeActive}
                                onToggleSingleMode={handleToggleSingleMode}
                                onTogglePalletMode={handleTogglePalletMode}
                                getFlavourQty={getFlavourQty}
                                getProductQty={getProductQty}
                                onAdjustQty={handleAdjustBoxQty}
                                onSetQty={handleSetSupportingQty}
                                onToggleBoxMode={handleToggleBoxMode}
                                showSingleTier={singleTubAllowed}
                                quantityMode={false}
                                enabled={false}
                                onToggleEnabled={handleToggleEnabled}
                                plusDisabled={bundleSatisfied}
                                lockNewSelections={lockNewSelections}
                                productDiscounts={productDiscounts}
                                showCalc={me?.role !== 'customer'}
                                dimmed={dimmed}
                              />
                            );
                          })}
                          </div>
                        </div>
                        );
                      })
                    : rangeProducts.map((product) => {
                    const isMultiFlavour = product.flavours?.length > 0;
                    const instances = selectedProducts.filter((p) => p.id === product.id);
                    const selected = instances[0];
                    const selectedFlavours = instances.map((p) => p.selectedFlavour).filter(Boolean);
                    const isFoundation = product.range === 'Core';
                    const boxSize = getFoundationBoxSize(product);
                    const boxModeActive = boxModeProductId === product.id;
                    const singleModeActive = singleModeProductId === product.id;
                    // Supporting cards ALWAYS use quantity steppers (+/− with typed entry) —
                    // duplicates of the same SKU are allowed within the tier rules.
                    const supportingQuantityMode = !isFoundation;
                    const supportingEnabled = !isFoundation && (enabledSupportingIds.has(product.id) || instances.length > 0);
                    const isLargeSaver = LARGE_SAVER_SIZES.has(proteinSize);
                    // TOTAL supporting cap applies to every tier (single/box/pallet, 1kg + 2kg).
                    // Small non-pallet savers additionally keep the per-range cap; large (2 kg)
                    // savers cap each SKU (product+flavour) at 1 per Core tub instead.
                    const capBlocked = !isFoundation && supportingCapReached;
                    const plusDisabled = capBlocked
                      || (!isFoundation
                          && !palletModeProductId
                          && !isLargeSaver
                          && isSupportingPlusDisabled(product, selectedProducts));
                    const skuCap = (!isFoundation && isLargeSaver && !palletModeProductId)
                      ? Math.max(1, foundationCount)
                      : null;
                    // Lock everything that isn't already selected once the bundle is satisfied
                    // — or, for supporting cards, once the total supporting cap is reached.
                    const lockNewSelections = (bundleSatisfied || capBlocked) && instances.length === 0;
                    // For Foundation in Box mode, the +/- stepper hits the cap at boxSize; we
                    // also disable when bundle is satisfied. For supporting, also lock new adds.
                    const effectivePlusDisabled = plusDisabled || bundleSatisfied;
                    // Build a tooltip explaining WHY the + is disabled — visible on hover and
                    // exposed to screen-readers via aria-label.
                    let plusDisabledReason = null;
                    if (effectivePlusDisabled) {
                      if (capBlocked) {
                        plusDisabledReason = `Supporting items are full (${supportingTotalCount}/${supportingCapTotal} across all categories). Remove an item to add more.`;
                      } else if (plusDisabled) {
                        const rangeItems = selectedProducts.filter((p) => p.range === product.range);
                        const aminos = rangeItems.filter((p) => p.name === 'Pure Series Aminos').length;
                        const effective = rangeItems.length - Math.max(0, aminos - 1);
                        plusDisabledReason = `${product.range} range is full (${effective}/${foundationCount}). Remove an item to add more.`;
                      } else if (bundleSatisfied) {
                        plusDisabledReason = 'Saver is complete — deselect an item before adding more.';
                      }
                    }
                    // Dim un-engaged supporting cards once the TOTAL cap (any tier) or this
                    // range's cap (small savers) is reached.
                    // "Engaged" = has instances OR the user has ticked the enable toggle.
                    const dimmed = (capBlocked
                        || (!isLargeSaver && supportingRangeFull(product.range)))
                      && instances.length === 0
                      && !enabledSupportingIds.has(product.id);
                    return (
                      <ProductCard
                        key={product.id}
                        product={product}
                        isSelected={instances.length > 0}
                        selectedFlavour={selected?.selectedFlavour}
                        selectedFlavours={selectedFlavours}
                        onSelect={handleSelectProduct}
                        onToggleFlavour={handleToggleFlavour}
                        multiFlavourMode={isMultiFlavour}
                        boxSize={boxSize}
                        boxMode={boxModeActive}
                        singleMode={singleModeActive}
                        onToggleSingleMode={handleToggleSingleMode}
                        getFlavourQty={getFlavourQty}
                        getProductQty={getProductQty}
                        onAdjustQty={handleAdjustBoxQty}
                        onSetQty={handleSetSupportingQty}
                        skuCap={skuCap}
                        onToggleBoxMode={handleToggleBoxMode}
                        showSingleTier={singleTubAllowed}
                        quantityMode={supportingQuantityMode}
                        enabled={supportingEnabled}
                        onToggleEnabled={handleToggleEnabled}
                        plusDisabled={effectivePlusDisabled}
                        plusDisabledReason={plusDisabledReason}
                        lockNewSelections={lockNewSelections}
                        productDiscounts={productDiscounts}
                        showCalc={me?.role !== 'customer'}
                        dimmed={dimmed}
                      />
                    );
                  })}
                </div>
              </section>
              );
            })}
          </div>

          <div>
            {activeContextCustomerId && (
              <div className="flex justify-end mb-3">
                <OrderTotalsPopover customerId={activeContextCustomerId} extraItems={selectedProducts} />
              </div>
            )}
            {customerSmartSaverEnabled && (
              <BundleSummaryCard
                selectedProducts={selectedProducts}
                bundleName={bundleName}
                setBundleName={setBundleName}
                bundleComments={bundleComments}
                setBundleComments={setBundleComments}
                validation={effectiveValidation}
                onSave={handleSaveBundle}
                discountPct={discountPct}
                productDiscounts={productDiscounts}
                tier={currentTier()}
                palletPct={pickDiscountPct(proteinSize, 'pallet')}
                boxPct={pickDiscountPct(proteinSize, 'box')}
                onSwitchTier={switchTierKeepingSelection}
              />
            )}

            <SavedBundlesList
              savedBundles={savedBundles}
              expandedBundleIds={expandedBundleIds}
              onToggleExpansion={toggleBundleExpansion}
              onExpandAll={() => setExpandedBundleIds(new Set(savedBundles.map((b) => b.id)))}
              onCollapseAll={() => setExpandedBundleIds(new Set())}
              onRemove={handleRemoveBundle}
              onDuplicate={handleDuplicateBundle}
              onClearAll={handleClearAll}
              onSubmitOrder={handleSubmitOrder}
              onSaveOrder={handleSaveOrder}
              onEdit={handleEditBundle}
              onExportAll={handleExportAll}
            />
          </div>
        </div>
      </div>

      <SelectionGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
      <ImportOrderPdfModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        customerId={activeContextCustomerId}
        onApplied={() => fetchSavedBundles()}
      />

      <ExportCsvModal
        exportModal={exportModal}
        copyStatus={copyStatus}
        onClose={() => { setExportModal(null); setCopyStatus('idle'); }}
        onDownload={downloadFromModal}
        onCopy={copyCsvToClipboard}
      />

      <AlertDialog
        open={!!notice}
        title={notice?.title}
        message={notice?.message}
        variant={notice?.variant || 'info'}
        onClose={() => setNotice(null)}
        testIdPrefix="bundle-builder-notice"
      />
    </div>
  );
};

export default BundleBuilder;
